import logo from "@static/img/logo-pink-bg.svg";
import { compact } from "lodash";
import { useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { TFLocationState } from "types/topicflow";

import { currentOrganizationVar, currentUserVar } from "@cache/cache";
import Button from "@components/button/button";
import Layout from "@components/layout/layout";
import { useLink } from "@components/link/link-helpers";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";
import useDocumentTitle from "@components/use-document-title/use-document-title";
import { classNames } from "@helpers/css";
import { getAssetUrl } from "@helpers/helpers";

import AskAI from "../ask-ai/ask-ai";
import ChatHistoryPopover from "./components/chat-history-popover";
import DirectReports from "./components/direct-reports";
import Insights from "./components/insights";
import HomePeople from "./components/people";

const Home = () => {
  const { replace } = useLink();
  const params = useParams<{ chatSessionId: string }>();
  const askAIPathName = "/ask-ai";
  const location = useLocation<TFLocationState>();
  const currentUser = currentUserVar();
  const currentOrganization = currentOrganizationVar();
  const [askAIChatSessionId, setAskAIChatSessionId] = useState<null | number>(
    params.chatSessionId ? parseInt(params.chatSessionId) : null,
  );
  const [startedChatSession, setStartedChatSession] = useState(
    !!askAIChatSessionId || location.pathname.includes(askAIPathName),
  );
  useDocumentTitle(startedChatSession ? "Ask AI" : "Home");
  const hasDirectReports = currentUser.directReports.edges.length > 0;

  const isAskAIEnabled = !!currentOrganization.featureFlags.askAi;
  const showNewDirectReports = waffle.flag_is_active(
    "new-homepage-direct-reports",
  );
  const initialPrompt =
    new URLSearchParams(location.search).get("p") || undefined;

  // Track the pathname from the previous render so the effect below can tell
  // whether the session id changed (new session created) or the pathname
  // changed (user navigated). See the foreground-rewrite guard for why.
  const prevPathnameRef = useRef(location.pathname);

  // when the session id changes, update the url
  useEffect(() => {
    const previousPathname = prevPathnameRef.current;
    prevPathnameRef.current = location.pathname;

    if (!askAIChatSessionId) return;

    const askAIChatSessionPath = `${askAIPathName}/${askAIChatSessionId}`;
    const isSessionlessAskAIPath =
      location.pathname === askAIPathName ||
      location.pathname === `${askAIPathName}/`;
    // True only when we were ALREADY on the sessionless /ask-ai path, i.e. a new
    // session id was just created. False when the user navigated TO /ask-ai from
    // an existing session (e.g. clicked the "new chat" button): in that case the
    // session id in state is stale and about to be cleared by the sibling effect,
    // so rewriting the URL forward would clobber the navigation and undo it.
    const wasAlreadySessionless =
      previousPathname === askAIPathName ||
      previousPathname === `${askAIPathName}/`;
    const background = location.state?.background;
    const backgroundIsAskAI =
      background?.pathname === askAIPathName ||
      background?.pathname.startsWith(`${askAIPathName}/`);
    const shouldUpdateBackground =
      backgroundIsAskAI && background?.pathname !== askAIChatSessionPath;
    const shouldUpdateForeground =
      !background &&
      // Only auto-rewrite the URL while the user is still on an /ask-ai route.
      // Without this guard, navigating to /dashboard (or any other page) while
      // a session was open would immediately replace the URL back to /ask-ai/<id>
      // because askAIChatSessionId hasn't been cleared yet by the sibling effect.
      isSessionlessAskAIPath &&
      wasAlreadySessionless;

    if (shouldUpdateBackground && background) {
      replace({
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
        state: {
          ...location.state,
          background: {
            ...background,
            pathname: askAIChatSessionPath,
          },
        },
      });
    } else if (shouldUpdateForeground) {
      replace(askAIChatSessionPath);
    }
  }, [askAIChatSessionId, location, replace]);

  // when the url changes, update the session id
  useEffect(() => {
    setStartedChatSession(location.pathname.includes(askAIPathName));

    if (askAIChatSessionId && !params.chatSessionId) {
      setAskAIChatSessionId(null);
    } else if (
      params.chatSessionId &&
      (!askAIChatSessionId ||
        params.chatSessionId !== String(askAIChatSessionId))
    ) {
      setAskAIChatSessionId(parseInt(params.chatSessionId));
    }
  }, [params.chatSessionId, location.pathname]);

  // Prefer URL params (synchronous on navigation) over state (updated
  // asynchronously via useEffect) to avoid a one-frame flicker when
  // switching sessions via the chat-history popover links.
  const effectiveChatSessionId = params.chatSessionId
    ? parseInt(params.chatSessionId)
    : askAIChatSessionId;

  const fullAskAIView =
    isAskAIEnabled && (startedChatSession || !!effectiveChatSessionId);

  const userName = currentUser.firstName || null;
  return (
    <Layout>
      {isAskAIEnabled && (
        <div className="absolute z-1 top-2 right-4 flex items-center gap-4">
          <Button to="/ask-ai" icon={getTopicflowIcon("pencilSquare")} />
          <ChatHistoryPopover />
        </div>
      )}
      <Layout.Container noHeader={fullAskAIView}>
        <Layout.Main
          subClassName={classNames(
            "flex flex-col h-full relative",
            fullAskAIView ? "pb-0" : "",
          )}
          className={classNames(fullAskAIView ? "pt-0 md:pt-0" : "")}
          width={fullAskAIView ? "full-no-padding" : "default"}
        >
          {!fullAskAIView && (
            <div className="mt-0  tf-header-2xl font-semibold tracking-tight flex items-center gap-3 flex-col justify-center">
              <img
                src={getAssetUrl(logo)}
                alt="Topicflow"
                className="block h-10 w-10"
              />
              <div>Welcome back{userName ? `, ${userName}` : ""}!</div>
            </div>
          )}

          {isAskAIEnabled && (
            <AskAI
              isInSidebar={false}
              chatSessionId={effectiveChatSessionId}
              onChangeChatSessionId={setAskAIChatSessionId}
              onStartChatSession={() => setStartedChatSession(true)}
              className={classNames(!fullAskAIView && "mt-6")}
              isFullScreen={fullAskAIView}
              totalYPaddingAroundContainer={0}
              initialPrompt={initialPrompt}
              formButtons={compact([
                {
                  text: hasDirectReports ? "Team Updates" : "My Updates",
                  prompt: hasDirectReports
                    ? "Give me an overview of my direct reports for the past 7 days."
                    : "Give me an overview of my work for the past 7 days.",
                  icon: hasDirectReports ? "users" : "user",
                },
              ])}
            />
          )}

          {!fullAskAIView && (
            <div className="py-10 flex flex-col gap-10">
              {showNewDirectReports && <DirectReports />}
              <Insights selectedUser={currentUser} />
              {!showNewDirectReports && <HomePeople />}
            </div>
          )}
        </Layout.Main>
      </Layout.Container>
    </Layout>
  );
};

export default Home;
